// H-017 — vista de consulta minimizada con revelado auditado. NO es el «Modo Consulta» de licencia (`modoConsulta.ts`, solo lectura por licencia impaga).
// Muestra de la ficha solo lo mínimo (iniciales, edad y género, identificación enmascarada); ver el dato completo exige elegir un MOTIVO y el servidor deja
// constancia en la bitácora inmutable (`POST /api/hce/revelar-dato`, fail-closed: sin constancia no se muestra). Aquí no se guarda ningún dato del paciente.
import { cabecerasBoveda } from './audit/auditedFetch';
import { vaultUrl } from './hceVault';

/** Los mismos códigos que acepta el servidor (packages/web/src/api/lib/hce-revelar-dato.ts); el texto lo pone `fichaPaciente.minimizada.motivos`. */
export const MOTIVOS_REVELADO = ['identificar-paciente', 'contactar-paciente', 'verificar-datos', 'tramite-administrativo', 'otro-asistencial'] as const;
export type MotivoRevelado = (typeof MOTIVOS_REVELADO)[number];
export type CampoRevelable = 'nombre' | 'identificacion' | 'nacimiento' | 'telefono';

/** Cuánto dura un revelado en pantalla antes de volver a ocultarse. */
export const DURACION_REVELADO_MS = 5 * 60_000;

const CLAVE_PREFERENCIA = 'hce.vistaMinimizada';

/** `1712345678` -> `17******78`; por debajo de 6 caracteres oculta todo. */
export function identificacionMinima(valor: string | undefined): string {
  const v = (valor ?? '').trim();
  if (!v) return '';
  if (v.length < 6) return '*'.repeat(v.length);
  return `${v.slice(0, 2)}${'*'.repeat(v.length - 4)}${v.slice(-2)}`;
}

/** Preferencia por dispositivo (no es un dato clínico). Sin almacenamiento disponible, apagada. */
export function leerPreferenciaMinimizada(): boolean {
  try {
    return localStorage.getItem(CLAVE_PREFERENCIA) === '1';
  } catch {
    return false;
  }
}

export function guardarPreferenciaMinimizada(activa: boolean): void {
  try {
    if (activa) localStorage.setItem(CLAVE_PREFERENCIA, '1');
    else localStorage.removeItem(CLAVE_PREFERENCIA);
  } catch {
    // sin almacenamiento: la vista funciona igual, solo no se recuerda
  }
}

export type ResultadoRevelado = { ok: true } | { ok: false; error: string };

/** Pide al servidor la constancia del revelado. `ok: true` solo si quedó anotado; cualquier otra cosa = no mostrar el dato. */
export async function solicitarRevelado(pacienteId: string, campos: readonly CampoRevelable[], motivo: MotivoRevelado): Promise<ResultadoRevelado> {
  try {
    const res = await fetch(vaultUrl('/api/hce/revelar-dato'), {
      method: 'POST',
      headers: cabecerasBoveda(),
      body: JSON.stringify({ paciente: pacienteId, campos, motivo }),
    });
    if (res.ok) return { ok: true };
    const cuerpo = (await res.json().catch(() => ({}))) as { error?: unknown };
    return { ok: false, error: typeof cuerpo.error === 'string' && cuerpo.error ? cuerpo.error : 'No se pudo dejar constancia del revelado.' };
  } catch {
    return { ok: false, error: 'No se pudo conectar. Revisa tu conexión e inténtalo de nuevo.' };
  }
}
